import * as React from 'react';
import { Query } from 'react-apollo';
import update from 'immutability-helper';
import { findMatchingRecord, findMatchingRecordIndex } from 'utils/arrayHelpers';
import ACTIVE_GROUPS_QUERY from './queries/ActiveGroupsQuery.gql';

import {
  ActiveGroupsQuery,
  ActiveGroupsQueryVariables,
  ActiveGroupsQuery_organizations,
  ActiveGroupsQuery_organizations_classes_classes,
} from './queries/__generated__/ActiveGroupsQuery';

import { GroupRow, GroupOwnerRole } from 'groups/components/GroupList/GroupListTable';
import { Props } from 'groups/containers/GroupList/GroupsListFetcher';

function translateActiveGroup(group: ActiveGroupsQuery_organizations_classes_classes): GroupRow {
  return {
    uuid: group.uuid,
    avatarSvgUrl: group.groupAvatar.svgUrl,
    className: group.className,
    createdAt: group.createdAt,
    messagesCount: group.messagesCount,
    owners: group.owners.map(owner => owner.user),
    participantsCount: group.subscribersCount,
  };
}

export default function ActiveGroupsListFetcher(props: Props) {
  const { orgUuid, query, selectedRoles, sortDirection, sortType, handleError } = props;

  return (
    <Query<ActiveGroupsQuery, ActiveGroupsQueryVariables>
      query={ACTIVE_GROUPS_QUERY}
      variables={{
        orgUuid,
        first: 20,
        after: null,
        query,
        sortType,
        sortDirection,
      }}
      fetchPolicy="network-only">
      {({ data, error, fetchMore, loading, refetch }) => {
        if (error) {
          handleError();
          return null;
        }

        if (!data) {
          return props.children({ loading, hasNextPage: false });
        }

        const org = findMatchingRecord<ActiveGroupsQuery_organizations>(
          data.organizations,
          orgUuid
        );

        if (!org || !org.classes) {
          return props.children({ loading, hasNextPage: false });
        }

        const {
          classes,
          pageInfo: { endCursor, hasNextPage },
        } = org.classes;

        const handleFetchMore = () =>
          fetchMore({
            variables: {
              after: endCursor,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult) {
                return prev;
              }

              const fetchMoreContents = findMatchingRecord(fetchMoreResult.organizations, orgUuid);
              const prevContents = findMatchingRecord(prev.organizations, orgUuid);
              const index = findMatchingRecordIndex(prev.organizations, orgUuid);

              if (!fetchMoreContents || !prevContents || index === undefined || index === null) {
                return prev;
              }

              return update(prev, {
                organizations: {
                  [index]: {
                    classes: {
                      classes: {
                        $set: [
                          ...prevContents.classes.classes,
                          ...fetchMoreContents.classes.classes,
                        ],
                      },
                      pageInfo: { $set: fetchMoreContents.classes.pageInfo },
                    },
                  },
                },
              });
            },
          });

        const groups = classes
          .filter(
            group =>
              group.owners.filter(
                owner =>
                  owner.user?.role && selectedRoles.includes(owner.user.role as GroupOwnerRole)
              ).length > 0
          )
          .map((group: ActiveGroupsQuery_organizations_classes_classes) =>
            translateActiveGroup(group)
          );

        return props.children({ groups, handleFetchMore, loading, hasNextPage, refetch });
      }}
    </Query>
  );
}
